// Traduções da interface: português (padrão), inglês, espanhol e japonês.
// - Textos da UI: src/locales/<idioma>.ts (o pt-BR define as chaves; os demais
//   são verificados pelo TypeScript: faltou chave = erro de compilação).
// - Dados do jogo (sinergias, habilidades, itens, cartas): data/i18n/*.yaml,
//   gerados em packages/shared/generated/i18n.json (ver data.ts).
// - Nomes oficiais (Pokémon, golpes, itens): PokéAPI (npm run i18n:names).
import pt, { type Messages } from "./locales/pt-BR";
import en from "./locales/en";
import es from "./locales/es";
import ja from "./locales/ja";

export type Lang = "pt-BR" | "en" | "es" | "ja";
export const LANGS: { id: Lang; label: string }[] = [
  { id: "pt-BR", label: "Português" },
  { id: "en", label: "English" },
  { id: "es", label: "Español" },
  { id: "ja", label: "日本語" },
];
const DICTS: Record<Lang, Messages> = { "pt-BR": pt, en, es, ja };
const KEY = "pokechess:lang";

function detect(): Lang {
  try {
    const saved = localStorage.getItem(KEY) as Lang | null;
    if (saved && saved in DICTS) return saved;
  } catch {
    /* sem storage */
  }
  for (const l of navigator.languages ?? [navigator.language]) {
    const s = l.toLowerCase();
    if (s.startsWith("pt")) return "pt-BR";
    if (s.startsWith("es")) return "es";
    if (s.startsWith("ja")) return "ja";
    if (s.startsWith("en")) return "en";
  }
  return "pt-BR";
}

let current: Lang = detect();
const listeners = new Set<(l: Lang) => void>();

export function lang(): Lang {
  return current;
}

export function setLang(l: Lang) {
  if (l === current) return;
  current = l;
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* ok */
  }
  applyDocumentLang();
  for (const f of listeners) f(l);
}

export function onLangChange(f: (l: Lang) => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** Atualiza <html lang> e carrega a fonte japonesa só quando precisa. */
export function applyDocumentLang() {
  document.documentElement.lang = current;
  if (current === "ja" && !document.getElementById("font-ja")) {
    const link = document.createElement("link");
    link.id = "font-ja";
    link.rel = "stylesheet";
    link.href = "/fonts/ja/dotgothic16.css";
    document.head.appendChild(link);
  }
}

export type MsgKey = keyof Messages;

/** Texto traduzido; {nome} é trocado por params.nome. */
export function t(key: MsgKey, params?: Record<string, string | number>): string {
  const s = DICTS[current][key] ?? pt[key] ?? key;
  return params ? s.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`)) : s;
}

/** Número formatado no idioma atual (ex.: 1.400 / 1,400). */
export function num(n: number): string {
  return new Intl.NumberFormat(current).format(n);
}

/** Valor em reais formatado no idioma atual. */
export function brl(cents: number): string {
  return new Intl.NumberFormat(current, { style: "currency", currency: "BRL" }).format(cents / 100);
}
